/**
 * Development overlay.
 *
 * Shows exactly what the brief asks a debug mode to expose: the registered
 * template, the live stroke, the similarity score, the recognised chant, the
 * speech-onset timestamp, the server's verdict, and the network offset. Hidden
 * from the shipping HUD — it is behind the `showDebug` setting and the ` key.
 */
import { useEffect, useRef } from 'react';
import { scoreAttempt } from '@spell/gesture-engine';
import { TIMING } from '@spell/shared-types';
import { net } from '../networking/net';
import { useNet, selectGuessedSpell } from '../networking/netStore';
import { castState, useCastUi } from '../gameplay/castState';
import { combatRecorder } from '../gesture/capture';
import { scene as bus } from '../scene/sceneBus';
import { micLevels } from '../voice/mic';

export function DebugOverlay() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rows = useRef<Record<string, HTMLSpanElement | null>>({});
  const lastResult = useCastUi((s) => s.lastResult);

  useEffect(() => {
    let raf = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const state = useNet.getState();
      // Whatever the live guess currently believes is being cast.
      const spell = selectGuessedSpell(state, castState.guessId);

      set('fps', bus.fps.toFixed(0));
      set('rtt', `${Math.round(net.rtt)}ms`);
      set('offset', `${net.clockOffset.toFixed(1)}ms`);
      set('phase', `${state.phase} r${state.round}`);
      set('projectiles', String(bus.projectiles.length));
      set('danger', bus.danger.toFixed(2));
      set('mic', micLevels.level.toFixed(3));
      set('onset', castState.onsetAt ? `${(castState.onsetAt % 100000).toFixed(0)}` : '—');
      set('transcript', castState.transcript || '—');
      set('guess', spell ? `${spell.chant} ${castState.guessScore.toFixed(2)}` : '—');
      set('window', `${TIMING.PARRY_WINDOW_MS}/-${TIMING.PARRY_LATENCY_GRACE_MS}ms`);

      let live = '—';
      if (spell?.gesture && combatRecorder.points.length > 4) {
        const s = scoreAttempt(combatRecorder.points, spell.gesture);
        live = `${s.score.toFixed(2)} (모양 ${s.shape.toFixed(2)} 방향 ${s.direction.toFixed(2)} 곡률 ${s.curvature.toFixed(2)})`;
      }
      set('gesture', live);

      // Template + live stroke.
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (canvas && ctx) {
        const w = canvas.width;
        const h = canvas.height;
        ctx.clearRect(0, 0, w, h);
        const project = (x: number, y: number): [number, number] => [
          w / 2 + x * (w / 2.6),
          h / 2 + y * (h / 2.6),
        ];
        if (spell?.gesture) {
          ctx.strokeStyle = 'rgba(217,188,118,0.75)';
          ctx.lineWidth = 2;
          ctx.beginPath();
          let last = spell.gesture.points[0]?.s ?? 0;
          spell.gesture.points.forEach((p, i) => {
            const [px, py] = project(p.x, p.y);
            if (i === 0 || p.s !== last) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
            last = p.s;
          });
          ctx.stroke();
        }
        const pts = combatRecorder.points;
        if (pts.length > 1) {
          const maxAbs = pts.reduce((m, p) => Math.max(m, Math.abs(p.x), Math.abs(p.y)), 0.001);
          ctx.strokeStyle = '#7fe0a8';
          ctx.lineWidth = 2;
          ctx.beginPath();
          let last = pts[0].s;
          pts.forEach((p, i) => {
            const [px, py] = project(p.x / maxAbs, p.y / maxAbs);
            if (i === 0 || p.s !== last) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
            last = p.s;
          });
          ctx.stroke();
        }
      }
    };

    const set = (key: string, value: string) => {
      const el = rows.current[key];
      if (el && el.textContent !== value) el.textContent = value;
    };

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const Row = ({ id, label }: { id: string; label: string }) => (
    <div className="debug__row">
      <span>{label}</span>
      <span
        ref={(el) => {
          rows.current[id] = el;
        }}
      >
        —
      </span>
    </div>
  );

  return (
    <div className="debug" data-testid="debug-overlay">
      <div style={{ color: 'var(--gold)', marginBottom: 4 }}>DEBUG (` 로 토글)</div>
      <Row id="fps" label="fps" />
      <Row id="rtt" label="rtt" />
      <Row id="offset" label="clock offset" />
      <Row id="phase" label="phase" />
      <Row id="projectiles" label="projectiles" />
      <Row id="danger" label="danger" />
      <Row id="mic" label="mic rms" />
      <Row id="onset" label="speech onset" />
      <Row id="guess" label="live guess" />
      <Row id="transcript" label="transcript" />
      <Row id="gesture" label="gesture score" />
      <Row id="window" label="parry window" />
      <div className="debug__row">
        <span>server verdict</span>
        <span>
          {lastResult
            ? `${lastResult.grade}${lastResult.reason ? ` (${lastResult.reason})` : ''}${
                lastResult.parry ? ` ${Math.round(lastResult.timingError)}ms` : ''
              }`
            : '—'}
        </span>
      </div>
      <canvas ref={canvasRef} width={266} height={120} />
      <div style={{ marginTop: 4, color: 'var(--ink-faint)' }}>금색 = 등록 템플릿 · 초록 = 현재 입력</div>
    </div>
  );
}
